import type { ReactElement } from "react";
import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { OnOffSwitch, OnOffSwitchSize } from "@similarweb/ui-components";

import { hasCrmPermissions } from "app/components/SideBar/utils";
import { translate } from "app/filters/i18nFilter";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { selectCrmLastSynced } from "app/pages/sales-intelligence/modules/leads-search-filters";
import CrmFieldsSelect from "app/pages/sales-intelligence/modules/leads-search-filters/components/CrmFieldsSelect/CrmFieldsSelect";
import { FilterContentContainer } from "app/pages/sales-intelligence/modules/leads-search-filters/components/filters/styles";
import LastUpdatedDate from "app/pages/sales-intelligence/modules/leads-search-filters/components/LastUpdatedDate/LastUpdatedDate";
import { useCrmFieldsFilter } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useCrmFilterLogic";
import type ExcludeByCrmFilter from "app/pages/sales-intelligence/modules/leads-search-filters/models/ExcludeByCrmFilter/ExcludeByCrmFilter";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import {
    selectIsCrmFilterEnabled,
    selectIsCrmNotIntegrated,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/crmSettingsSelectors";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";

import ConnectionLink from "./ConnectionLink/ConnectionLink";
import { FILTER_TRANSLATION_KEYS, OWNER_NAMES } from "./const";

const ExcludeByCrmFilterContainer = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseProps<ExcludeByCrmFilter>): ReactElement | null => {
    const { integrationName, fields } = filter.options;

    const [excludeSwitchState, setExcludeSwitchState] = useState<boolean>(false);

    useEffect(() => {
        setExcludeSwitchState(Boolean(value));
    }, [value]);

    const handleExcludeSwitchStateUpdate = () => {
        const newExcludeSwitchState = !excludeSwitchState;
        setExcludeSwitchState(newExcludeSwitchState);

        if (newExcludeSwitchState) {
            onFilterUpdate(filter.getFullSelection());
        } else {
            onFilterUpdate();
        }
    };

    const { updateFieldSelection, fieldLabels, getSelectedValues } = useCrmFieldsFilter({
        integrationName,
        fields,
        value,
        onFilterUpdate,
        inclusionOption: inclusionOptionMap.excludeOnly,
        labelOptions: {
            prefixKey: FILTER_TRANSLATION_KEYS.accountPrefix(integrationName),
            selectKey: FILTER_TRANSLATION_KEYS.select,
        },
    });

    const enhancedFieldLabels = {
        ...fieldLabels,
        [OWNER_NAMES]: {
            placeholder: translate(FILTER_TRANSLATION_KEYS.ownerPlaceholder),
            label: translate(FILTER_TRANSLATION_KEYS.ownerLabel(integrationName)),
        },
    };

    const isNotIntegrated = useSelector((state) => selectIsCrmNotIntegrated(state, integrationName));
    const isExcludeEnabled = useSelector((state) => selectIsCrmFilterEnabled(state, integrationName));
    const lastSynced = useSelector((state) => selectCrmLastSynced(state, integrationName));
    const hasAccess = hasCrmPermissions();
    const crmName = getCrmName(integrationName);

    if (isNotIntegrated) {
        return (
            <ConnectionLink
                hasAccess={hasAccess}
                onClick={() => siNavigator.goToManagementPage()}
                tooltipContent={translate("si.lead_gen_filters.category.exclude.notConnected.link.tooltip", {
                    integrationName: crmName,
                })}
                content={translate("si.lead_gen_filters.notConnected.text", {
                    integrationName: crmName,
                })}
            />
        );
    }

    if (!isExcludeEnabled) {
        return (
            <ConnectionLink
                hasAccess={hasAccess}
                onClick={() => siNavigator.goToCrmManagementPage({ crm: integrationName })}
                tooltipContent={translate(
                    `si.lead_gen_filters.category.exclude.${integrationName}.link.tooltip.enable_exclusion`,
                )}
                content={translate(`si.lead_gen_filters.category.exclude.${integrationName}.link.enable_exclusion`)}
            />
        );
    }

    return (
        <FilterContentContainer>
            <OnOffSwitch
                isSelected={excludeSwitchState}
                onLabel={translate(`si.lead_gen_filters.category.exclude.${integrationName}.toggle.label`)}
                onClick={handleExcludeSwitchStateUpdate}
                size={OnOffSwitchSize.small}
            />
            <CrmFieldsSelect
                fields={fields}
                fieldLabels={enhancedFieldLabels}
                integrationName={integrationName}
                lastSynced={lastSynced}
                onSelectionChange={updateFieldSelection}
                getSelectedValues={getSelectedValues}
                disabled={!excludeSwitchState}
            />
            {lastSynced ? <LastUpdatedDate date={lastSynced} /> : null}
        </FilterContentContainer>
    );
};

export default ExcludeByCrmFilterContainer;
